import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCcw, Edit2, Package } from "lucide-react";

import { fmtCurrency } from "../../utils/format";
import { Alert } from "../../components/Alert";
import offersIcon from "../../assets/offers-icon.png";
import { useProducts } from "./hooks/useProducts";
import { EditProductModal } from "../../components/EditProductModal";

const formatPercent = (value) => {
  if (!Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}%`;
};

export const AllegroProductCollection = () => {
  const { products, loading, message, setMessage, refreshProducts, updateProduct } = useProducts();
  const [editDialog, setEditDialog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message, setMessage]);

  const handleDialogSubmit = useCallback(
    async (value) => {
      if (!editDialog) return;
      const { product, mode } = editDialog;

      if (mode === "price") {
        const formatted = Number(value).toFixed(2);
        await updateProduct(product, { current_price: formatted }, "Price saved and sent to Allegro.");
      } else {
        await updateProduct(product, { available: Number(value) }, "Quantity saved and sent to Allegro.");
      }

      setEditDialog(null);
    },
    [editDialog, updateProduct]
  );

  const closeEditDialog = useCallback(() => setEditDialog(null), []);

  const openEditDialog = useCallback((mode, product) => {
    setEditDialog({ mode, product });
  }, []);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) => {
      const haystack = [product.name, product.sku, product.offer_id, product.ean]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [products, searchTerm]);

  const tableRows = useMemo(
    () =>
      filteredProducts.map((product) => ({
        ...product,
        formattedPrice: fmtCurrency(product.current_price, product.currency),
        linkedProduct: product.product_details,
      })),
    [filteredProducts]
  );

  const hasProducts = tableRows.length > 0;

  return (
    <div className="bg-white rounded-xl shadow p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <img
            src={offersIcon}
            alt="Offers"
            className="mr-2 h-7 w-7 sm:h-8 sm:w-8 object-contain"
          />
          Offers
        </h2>

        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-md px-2 py-1.5 shadow-sm">
            <Package size={16} className="text-gray-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search offers..."
              className="outline-none border-none bg-transparent placeholder:text-gray-400 text-sm min-w-[160px]"
            />
          </label>
          <button
            onClick={refreshProducts}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-60"
          >
            <RefreshCcw size={16} />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {message && <Alert kind={message.kind}>{message.text}</Alert>}

      {hasProducts ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Offer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Available
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tableRows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    <div>{row.name}</div>
                    <div className="text-xs text-gray-500">
                      <div>ID: {row.offer_id}</div>
                      <div>EAN: {row.ean || "none"}</div>
                    </div>
                    {row.linkedProduct ? (
                      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                        <div className="font-semibold">
                          Linked product: {row.linkedProduct.symbol || row.linkedProduct.ean || "—"}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-3 text-[0.65rem] text-emerald-800">
                          <span>Net {fmtCurrency(row.linkedProduct.net_price)}</span>
                          <span>Gross {fmtCurrency(row.linkedProduct.gross_price)}</span>
                          <span>VAT {formatPercent(row.linkedProduct.vat_rate)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-slate-500">No product linked</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.sku || "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.formattedPrice}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.available}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {row.publication_status || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <button
                      onClick={() => openEditDialog("price", row)}
                      className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <Edit2 size={14} />
                      Change price
                    </button>
                    <button
                      onClick={() => openEditDialog("quantity", row)}
                      className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 ml-2"
                    >
                      <Package size={14} />
                      Change quantity
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-gray-500 text-sm mt-6">
          No products. Run the Allegro import from Account Settings to fetch current offers.
        </div>
      )}
      {editDialog && (
        <EditProductModal
          mode={editDialog.mode}
          product={editDialog.product}
          onSubmit={handleDialogSubmit}
          onClose={closeEditDialog}
        />
      )}
    </div>
  );
};

export default AllegroProductCollection;
